import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Button, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import ExpressionField from 'app/shared/components/form/ExpressionField';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import FormGeneratorSwitchCDS from 'app/shared/componentsRestyled/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { signingCapacityOptions } from 'constants/formBuilder';
import useForm from 'hooks/useForm';
import { buildExpressionTree, formatExpression } from 'utils/expressions';

import { formsTestIds } from '../../../../forms.testIds';

import { getSchema } from './SigningRuleModal.utils';

const SigningRuleModal = ({ rule, schemaParams, onSave, onCancel }) => {
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const initialForm = useMemo(
    () => ({
      expression: rule?.expression || '',
      capacity: rule?.capacity || null,
      includeInESigning: !!rule?.includeInESigning,
    }),
    [rule],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    staticValidateField,
  } = useForm(initialForm);

  useEffect(() => {
    if (!schemaParams?.entities) {
      return;
    }

    setSchema(getSchema({ schemaParams, isNewExpressionsParsingLogicEnabled }));
  }, [setSchema, schemaParams, isNewExpressionsParsingLogicEnabled]);

  const onAutocompleteChange = useCallback(
    (e, value) => onAutocompleteChangeInForm({ setInForm, validateField })(e, value),
    [setInForm, validateField],
  );

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const expressionTree = buildExpressionTree(form.expression, {
        schemaParams,
        isOnlyBooleanAllowed: true,
        isNewExpressionsParsingLogicEnabled,
      });

      const formattedExpression = formatExpression(form.expression) || form.expression;

      onSave({
        id: rule?.id || crypto.randomUUID(),
        order: rule?.order || 0,
        expression: formattedExpression,
        expressionTree,
        capacity: form.capacity,
        includeInESigning: !!form.includeInESigning,
      });
    },
    [validateForm, form, schemaParams, isNewExpressionsParsingLogicEnabled, onSave, rule],
  );

  return (
    <form noValidate className="flex flex-col gap-24 p-24 min-w-[500px]" onSubmit={handleSubmit}>
      <ExpressionField
        isOnlyBooleanAllowed
        error={!!validationErrors.expression}
        handleChange={handleChange}
        helperText={validationErrors.expression || ''}
        label="Expression"
        name="expression"
        schemaParams={schemaParams}
        setInForm={setInForm}
        staticValidateField={staticValidateField}
        validateField={validateField}
        value={form.expression || ''}
      />

      <AutocompleteCDS
        autoHighlight
        required
        dataTestId={formsTestIds.SIGNING_RULE_MODAL_CAPACITY_SELECT}
        error={!!validationErrors.capacity}
        helperText={validationErrors.capacity}
        label="Signing Capacity"
        name="capacity"
        options={signingCapacityOptions}
        value={form.capacity}
        onChange={onAutocompleteChange}
      />

      <Typography color="text.secondary" variant="regular12">
        Determines how the signature block is formatted for matching signers
      </Typography>

      <FormGeneratorSwitchCDS
        checked={!!form.includeInESigning}
        label="Include in E-Signing"
        name="includeInESigning"
        onChange={handleChange}
      />

      <div className="flex justify-end items-center gap-12">
        <Button
          data-testid={formsTestIds.SIGNING_RULE_MODAL_CANCEL_BUTTON}
          type="button"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={formsTestIds.SIGNING_RULE_MODAL_SAVE_BUTTON}
          disabled={!isFormChanged}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

SigningRuleModal.propTypes = {
  schemaParams: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  rule: PropTypes.shape({
    id: PropTypes.string,
    order: PropTypes.number,
    expression: PropTypes.string,
    expressionTree: PropTypes.string,
    capacity: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    includeInESigning: PropTypes.bool,
  }),
};

export default memo(SigningRuleModal);
